import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Video from '@/components/atoms/video/video';
import Splash from '@/components/molecules/splash/splash';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import Column, {
  ColumnBreakPoints,
} from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import Row from '@/components/utilities/row/row';
import { formatImageUrl } from '@/libraries/formatters/format-image-url';
import styles from './header-carousel-item.module.scss';

export interface HeaderCarouselItemProps {
  /** The type of item to show */
  $type:
    | 'HeaderCarouselOverlaySlideDto'
    | 'HeaderCarouselSlideDto'
    | 'HeaderCarouselMaskedSlideDto'
    | 'HeaderCarouselSimpleSlideDto';
  /** The class name to apply */
  className?: string;
  /** An array of links to show */
  ctaLinks?: Content.Components['HeaderCarouselSlideDto']['ctaLinks'];
  /** Current index of the carousel*/
  currentIndex?: number;
  /** The splash in the first position */
  firstSplash?: Content.Components['HeaderCarouselSlideDto']['firstSplash'];
  /** Indicates whether the search bar is visible */
  hasSearchBar?: boolean;
  /** The text to show as a headline */
  headline?: string | null;
  /** Indicates whether autoplay is active */
  isPlaying?: boolean;
  /** Indicates whether the item is the primary item, used for SEO purposes */
  isPrimary?: boolean;
  /** The text to show as location */
  locationText?: Content.Components['HeaderCarouselSlideDto']['locationText'];
  /** The media to show */
  mediaItem?: Content.Components['HeaderCarouselSlideDto']['mediaItem'];
  /** Total number of the carousel items */
  pageCount?: number;
  /** The text to use as a caption */
  preHeadline?: Content.Components['HeaderCarouselSlideDto']['preHeadline'];
  /** The splash in the second position */
  secondSplash?: Content.Components['HeaderCarouselSlideDto']['secondSplash'];
  /** The settings for the component */
  settings?: Content.Components['HeaderCarouselSlideDto']['settings'];
}

const generateColumnConfiguration = ({
  hasMask,
  isCentered,
  isRightAligned,
}: {
  hasMask: boolean;
  isCentered: boolean;
  isRightAligned: boolean;
}): {
  offset: ColumnBreakPoints;
  width: ColumnBreakPoints;
  order: ColumnBreakPoints;
} => {
  if (isCentered) {
    return {
      offset: { desktop: 0, laptop: 0, mobile: 0, tablet: 0 },
      order: { mobile: 1, tablet: 1 },
      width: { desktop: 12, laptop: 12, mobile: 12, tablet: 12 },
    };
  }

  if (hasMask) {
    return {
      offset: {
        laptop: isRightAligned ? 0 : 1,
      },
      order: { mobile: 2, tablet: isRightAligned ? 1 : 2 },
      width: { laptop: 4, mobile: 9, tablet: 5 },
    };
  }

  return {
    offset: {
      desktop: isRightAligned ? 6 : 1,
      tablet: isRightAligned ? 5 : 1,
    },
    order: { mobile: 2, tablet: isRightAligned ? 1 : 2 },
    width: { laptop: 4, mobile: 9, tablet: 5 },
  };
};

const HeaderCarouselItem = ({
  $type,
  className,
  ctaLinks = [],
  firstSplash,
  hasSearchBar = false,
  headline,
  isPlaying = false,
  isPrimary = false,
  locationText,
  mediaItem,
  preHeadline,
  secondSplash,
  settings,
}: HeaderCarouselItemProps) => {
  const hasMask = $type === 'HeaderCarouselMaskedSlideDto';
  const hasOverlay = $type !== 'HeaderCarouselSimpleSlideDto';
  const isSimpleSlide = $type === 'HeaderCarouselSimpleSlideDto';
  const isLeftAligned = settings?.flippedLayout === true;
  const isRightAligned = settings?.flippedLayout !== true;
  const simpleSlideLinkUrl = ctaLinks[0]?.link?.url;
  const simpleSlideLinkTarget = ctaLinks[0]?.link?.target ?? undefined;

  const backgroundImage = mediaItem
    ? 'image' in mediaItem
      ? mediaItem.image
      : 'video' in mediaItem
        ? mediaItem.video.placeholderImage
        : undefined
    : undefined;

  const renderWrapper = () => {
    const wrapperContent = (
      <div className={styles.wrapper}>
        <div className={styles.splashContainer}>
          {!hasMask && firstSplash && (
            <Splash
              className={classNames(styles.splash, styles.isFirst)}
              {...firstSplash}
            />
          )}

          {!hasMask && secondSplash && (
            <Splash
              className={classNames(styles.splash, styles.isSecond)}
              {...secondSplash}
            />
          )}
        </div>

        {locationText && (
          <Container className={styles.locationContainer}>
            <Typography className={styles.location} tagStyle="labelMedium">
              {locationText}
            </Typography>
          </Container>
        )}

        <Container className={styles.textContainer}>
          <Row className={styles.textRow}>
            <Column
              className={styles.textColumn}
              {...generateColumnConfiguration({
                hasMask,
                isCentered: isSimpleSlide,
                isRightAligned,
              })}
            >
              <div className={styles.text}>
                {preHeadline && (
                  <Typography
                    className={styles.preHeadline}
                    tagStyle="labelMedium"
                  >
                    {preHeadline}
                  </Typography>
                )}

                {headline && (
                  <Typography
                    className={styles.headline}
                    tag={isPrimary ? 'h1' : 'h2'}
                    tagStyle="displayLarge"
                  >
                    {headline}
                  </Typography>
                )}
              </div>

              {!isSimpleSlide && ctaLinks.length > 0 && (
                <div className={styles.links}>
                  <CtaLinkList items={ctaLinks} />
                </div>
              )}
            </Column>

            {hasMask && (
              <Column
                className={styles.imageColumn}
                offset={{
                  desktop: isRightAligned ? 1 : 0,
                  tablet: isRightAligned ? 2 : 0,
                }}
                order={{ mobile: 1, tablet: isRightAligned ? 2 : 1 }}
                width={{ desktop: 7, tablet: 6 }}
              >
                <div className={styles.mask}>
                  {mediaItem && 'image' in mediaItem ? (
                    <Image
                      alt={mediaItem.image.altText}
                      className={styles.maskImage}
                      fill
                      focalPoint={mediaItem.image.focalPoint}
                      height={185}
                      src={mediaItem.image.url}
                      srcSet={{
                        desktop: {
                          height: 425,
                          width: 763,
                        },
                        laptop: {
                          height: 252,
                          width: 456,
                        },
                        tablet: {
                          height: 187,
                          width: 340,
                        },
                      }}
                      unoptimized
                      width={335}
                    />
                  ) : (
                    mediaItem &&
                    'video' in mediaItem && (
                      <Video
                        autoPlay
                        className={styles.maskVideo}
                        isPlaying={isPlaying}
                        placeholder={
                          mediaItem?.video?.placeholderImage?.url
                            ? formatImageUrl(
                                mediaItem?.video?.placeholderImage?.url,
                                {
                                  width: 500,
                                },
                              )
                            : undefined
                        }
                        src={mediaItem.video.url}
                      />
                    )
                  )}

                  <Image
                    alt=""
                    className={styles.tapeTopLeft}
                    height={111}
                    src="/images/header-carousel/tape-top-left.svg"
                    width={154}
                  />

                  <Image
                    alt=""
                    className={styles.tapeTopRight}
                    height={133}
                    src="/images/header-carousel/tape-top-right.svg"
                    width={144}
                  />

                  {firstSplash && (
                    <Splash
                      className={classNames(styles.splash, styles.isFirst)}
                      {...firstSplash}
                    />
                  )}

                  {secondSplash && (
                    <Splash
                      className={classNames(styles.splash, styles.isSecond)}
                      {...secondSplash}
                    />
                  )}
                </div>
              </Column>
            )}
          </Row>
        </Container>
      </div>
    );

    return isSimpleSlide && simpleSlideLinkUrl ? (
      <Link
        className={styles.wrapper}
        target={simpleSlideLinkTarget}
        url={simpleSlideLinkUrl}
      >
        {wrapperContent}
      </Link>
    ) : (
      wrapperContent
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasMask]: hasMask,
        [styles.hasSearchBar]: hasSearchBar,
        [styles.isLeftAligned]: isLeftAligned && !isSimpleSlide,
        [styles.isRightAligned]: isRightAligned && !isSimpleSlide,
        [styles.isCentered]: isSimpleSlide,
      })}
    >
      {!hasMask && mediaItem && 'video' in mediaItem ? (
        <Video
          autoPlay
          className={styles.video}
          isPlaying={isPlaying}
          placeholder={
            mediaItem?.video?.placeholderImage?.url
              ? formatImageUrl(mediaItem?.video?.placeholderImage?.url, {
                  width: 500,
                })
              : undefined
          }
          src={mediaItem.video.url}
        />
      ) : (
        backgroundImage && (
          <Image
            alt={backgroundImage.altText}
            className={styles.image}
            focalPoint={backgroundImage.focalPoint}
            height={552}
            priority
            src={backgroundImage.url}
            srcSet={{
              desktop: {
                height: 752,
                width: 1440,
              },
              laptop: {
                height: 620,
                width: 1024,
              },
              tablet: {
                height: 552,
                width: 769,
              },
            }}
            unoptimized
            width={375}
          />
        )
      )}

      {hasOverlay && <div className={styles.overlay} />}

      {renderWrapper()}

      <div className={styles.footer}>
        {firstSplash && (
          <Splash className={classNames(styles.splash)} {...firstSplash} />
        )}
      </div>
    </div>
  );
};

HeaderCarouselItem.displayName = 'HeaderCarouselItem';

export default HeaderCarouselItem;
